import { Button, IconButton } from "@rosc/ui/v32/button";
import { Checkbox } from "@rosc/ui/v32/checkbox";
import { SegmentedControl } from "@rosc/ui/v32/segmented-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rosc/ui/v32/select";
import { FlaskConical, X } from "lucide-react";
import type { JSX } from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PreChartLabs, ReportLabTest } from "@/lib/pre-chart-labs";
import { cn } from "@/lib/utils";

export type LabsChoice = "all" | "abnormal" | "custom";

// An attached lab report waiting to be added (clinical lead, 05/11/2026):
// which values (All values, Only abnormal values, Custom with the report's
// tests to tick) and the heading they go under. Replaces the pre-chart's
// lab chip.
export interface PreChartLabsOffer {
  count: number;
  // The files by name ("labs-oct.pdf").
  label: string;
  // The report's tests, for Custom (every ticked one is added).
  tests: readonly ReportLabTest[];
  headings: readonly string[];
  // Investigations (specialist) or Objective (SOAP).
  defaultHeading: string | null;
  // Where the choice starts: the clinician's "Pre-chart labs" setting.
  initialChoice: LabsChoice;
  initialPicks: readonly string[];
  onAdd: (labs: PreChartLabs, heading: string | null) => void;
  onDismiss: () => void;
}

// What a choice sends.
export const labsOfChoice = (
  choice: LabsChoice,
  picks: readonly string[],
): PreChartLabs =>
  choice === "custom" ? { mode: "tests", tests: [...picks] } : { mode: choice };

export const PreChartLabsHint = ({
  offer,
  className,
}: {
  offer: PreChartLabsOffer;
  className?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const [choice, setChoice] = useState<LabsChoice>(
    offer.tests.length ? offer.initialChoice : "all",
  );
  const [picks, setPicks] = useState<readonly string[]>(offer.initialPicks);
  const [heading, setHeading] = useState<string | null>(offer.defaultHeading);
  const custom = choice === "custom";
  const noneTicked = custom && picks.length === 0;
  const toggle = (name: string, on: boolean): void =>
    setPicks((current) =>
      on
        ? offer.tests
            .map((test) => test.name)
            .filter((test) => test === name || current.includes(test))
        : current.filter((test) => test !== name),
    );
  return (
    <section
      className={cn(
        "relative rounded-[16px] bg-v3-panel p-3 font-v3 shadow-v3-hairline-strong",
        className,
      )}
      data-testid="pre-chart-labs-hint"
      aria-labelledby={`${id}-title`}
    >
      <p className="min-w-0 pr-10 text-[13px] leading-[18px] lg:pr-8">
        <span className="font-medium text-v3-text" id={`${id}-title`}>
          {t("preChart.labsAdd.hintTitle", {
            count: offer.count,
            name: offer.label,
          })}
        </span>{" "}
        <span className="text-v3-text-2">{t("preChart.labsAdd.hint")}</span>
      </p>
      <SegmentedControl<LabsChoice>
        className="mt-2.5"
        label={t("preChart.labsAdd.choiceLabel")}
        segments={[
          {
            value: "all",
            label: t("preChart.labsAdd.segment.all"),
            testId: "pre-chart-labs-hint-all",
          },
          {
            value: "abnormal",
            // Phones: the short form fits the three segments.
            label: (
              <>
                <span className="sm:hidden">
                  {t("preChart.labsAdd.segment.abnormalShort")}
                </span>
                <span className="max-sm:hidden">
                  {t("preChart.labsAdd.segment.abnormal")}
                </span>
              </>
            ),
            testId: "pre-chart-labs-hint-abnormal",
          },
          ...(offer.tests.length
            ? [
                {
                  value: "custom" as const,
                  label: t("preChart.labsAdd.segment.custom"),
                  testId: "pre-chart-labs-hint-custom",
                },
              ]
            : []),
        ]}
        stretch
        value={choice}
        onValueChange={setChoice}
      />
      {custom && (
        <div className="mt-2" data-testid="pre-chart-labs-hint-tests">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[12.5px] leading-[17px] text-v3-text-3">
              {t("preChart.labsAdd.customHint", {
                picked: picks.length,
                total: offer.tests.length,
              })}
            </p>
            <Button
              className="h-8 px-2.5 text-[12.5px]"
              data-testid="pre-chart-labs-hint-toggle-all"
              variant="ghost"
              onClick={() =>
                setPicks(
                  picks.length === offer.tests.length
                    ? []
                    : offer.tests.map((test) => test.name),
                )
              }
            >
              {picks.length === offer.tests.length
                ? t("preChart.labsAdd.clear")
                : t("preChart.labsAdd.selectAll")}
            </Button>
          </div>
          <div className="mt-1 max-h-[min(36dvh,248px)] overflow-y-auto rounded-[12px] bg-v3-surface-2 px-3 py-1 shadow-v3-hairline sm:grid sm:grid-cols-2 sm:gap-x-4">
            {offer.tests.map((test) => (
              <Checkbox
                key={test.name}
                checked={picks.includes(test.name)}
                className="lg:min-h-8"
                label={
                  <span className="flex min-w-0 items-baseline justify-between gap-2">
                    <span className="min-w-0 truncate">{test.name}</span>
                    <span className="shrink-0 text-[12.5px] tabular-nums text-v3-text-3">
                      {test.value}
                    </span>
                  </span>
                }
                testId="pre-chart-labs-hint-test"
                onCheckedChange={(on) => toggle(test.name, on)}
              />
            ))}
          </div>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-end gap-2">
        {offer.headings.length > 0 && (
          <div className="min-w-0 flex-1 basis-[180px]">
            <label
              className="mb-1 block text-[12px] font-medium text-v3-text-3"
              htmlFor={`${id}-heading`}
            >
              {t("preChart.labsAdd.insertInto")}
            </label>
            <Select
              value={heading ?? ""}
              onValueChange={(next) => setHeading(next || null)}
            >
              <SelectTrigger
                className="h-10 bg-v3-panel"
                data-testid="pre-chart-labs-hint-heading"
                id={`${id}-heading`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {offer.headings.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                    {name === offer.defaultHeading && (
                      <span className="pl-2 text-[12px] text-v3-text-3">
                        {t("preChart.fileAdd.defaultHeading")}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button
          className="h-10 max-sm:w-full"
          data-testid="pre-chart-labs-hint-add"
          disabled={noneTicked}
          variant="secondary"
          onClick={() => offer.onAdd(labsOfChoice(choice, picks), heading)}
        >
          <FlaskConical aria-hidden />
          {t("preChart.labsAdd.add")}
        </Button>
      </div>
      <IconButton
        className="absolute right-1 top-1"
        data-testid="pre-chart-labs-hint-dismiss"
        label={t("preChart.fileAdd.hintDismiss")}
        onClick={offer.onDismiss}
      >
        <X />
      </IconButton>
    </section>
  );
};
